import { useMemo } from "react";
import { components, type MenuPortalProps, type GroupBase, type StylesConfig } from "react-select";

import { classNames } from "helpers/commonHelpers";
import Dropdown, { DropdownProps } from "components/v2/Dropdown/Dropdown";
import Select from "components/v2/Select/Select";
import type { FormSelectProps, SelectOption } from "components/v2/Select/types";

import {
  usePackingListMenuBoundary,
  usePackingListMenuLayout,
} from "@RequestFormV2/sections/PackingList/hooks/usePackingListMenuLayout";
import { filterPackingListSelectOption } from "@RequestFormV2/sections/PackingList/utils/filterPackingListSelectOption";

function PackingListMenuPortal(props: MenuPortalProps<SelectOption, false, GroupBase<SelectOption>>) {
  const { innerProps, ...rest } = props;

  return (
    <components.MenuPortal
      {...rest}
      innerProps={{
        ...innerProps,
        className: classNames("rfv2-packing-list-menu-portal", innerProps?.className),
      }}
    />
  );
}

function usePackingListSelectStyles(
  externalStyles?: StylesConfig<SelectOption>,
): StylesConfig<SelectOption> {
  return useMemo(
    () => ({
      ...externalStyles,
      menuPortal: (base, state) => {
        const fromConsumer = externalStyles?.menuPortal?.(base, state) ?? base;
        return {
          ...fromConsumer,
          width: fromConsumer.minWidth ?? fromConsumer.width,
          ...(externalStyles?.menuPortal ? { minWidth: undefined } : {}),
        };
      },
      menu: (base, state) => {
        const fromConsumer = externalStyles?.menu?.(base, state) ?? base;
        return {
          ...fromConsumer,
          width: "100%",
          minWidth: "unset",
        };
      },
    }),
    [externalStyles],
  );
}

export default function PackingListSelect({
  getMenuAnchor: getMenuAnchorProp,
  getMenuBoundary: getMenuBoundaryProp,
  menuAlign,
  menuFitContent = true,
  components: componentsProp,
  styles: stylesProp,
  filterOption: filterOptionProp,
  isSearchable,
  startCursorAtEnd,
  isClearable = false,
  ...props
}: FormSelectProps) {
  const getMenuAnchor = usePackingListMenuLayout();
  const getMenuBoundary = usePackingListMenuBoundary();
  const styles = usePackingListSelectStyles(stylesProp);
  const filterOption =
    filterOptionProp ?? (isSearchable !== false ? filterPackingListSelectOption : undefined);
  const enableStartCursorAtEnd = startCursorAtEnd ?? isSearchable !== false;

  return (
    <Select
      {...props}
      isClearable={isClearable}
      isSearchable={isSearchable}
      startCursorAtEnd={enableStartCursorAtEnd}
      filterOption={filterOption}
      styles={styles}
      menuFitContent={menuFitContent}
      menuAlign={menuAlign}
      getMenuAnchor={getMenuAnchorProp ?? getMenuAnchor}
      getMenuBoundary={
        menuAlign === "right" ? (getMenuBoundaryProp ?? getMenuBoundary) : getMenuBoundaryProp
      }
      components={{
        MenuPortal: PackingListMenuPortal,
        ...componentsProp,
      }}
    />
  );
}

export function PackingListDropdown(props: DropdownProps) {
  return <Dropdown {...props} alignRight />;
}
